import { Mic } from "assets/svg";
import classNames from "classnames";
import { Button } from "components/Button/Button";
import { ORGANIZATIONS, SPONTANEOUS_RECORDING_TIME } from "constant";
import { useCallback, useEffect, useRef, useState } from "react";
import { storage } from "services/storage";
import stopAudio from "../../assets/icons/stop.svg"
import AudioRipple from "./AudioRipple";

export const AudioRecorder = ({
  questionType = null,
  onDataAvailable = () => { },
  loading = false,
  hideTimer = false,
  showMicVolumeLevel = true,
  audioDeviceId = storage.get.audioDevice(),
  getBlobData = () => { },
  onStatusChange = () => { },
  onSubmit = () => { },
  timeToSolve = SPONTANEOUS_RECORDING_TIME,
  isOffline = false,
  gameTime = null
}) => {

  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Saving")
  const [recordTimer, setRecordTimer] = useState(SPONTANEOUS_RECORDING_TIME);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [audioContext, setAudioContext] = useState(null);
  const [isValidAudio, setIsValidAudio] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [isTimerReady, setIsTimerReady] = useState(false)

  const buttonRef = useRef(null);
  const timerRef = useRef(null);
  const recordedAudioFile = useRef(null);
  const workerRef = useRef(null)

  const organization = storage.get.org();
  const spotlightOrgFlag = organization === ORGANIZATIONS.SPOTLIGHT

  const requestRecorder = useCallback(async () => {
    const deviceId = audioDeviceId;
    const constraints = { audio: deviceId ? { deviceId } : true };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);

    // logic for showing microphone input level
    if (showMicVolumeLevel) {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      const scriptProcessor = audioContext.createScriptProcessor(2048, 1, 1);

      analyser.smoothingTimeConstant = 0.8;
      analyser.fftSize = 1024;

      microphone.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(audioContext.destination);
      scriptProcessor.onaudioprocess = function () {
        const array = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(array);
        const arraySum = array.reduce((a, value) => a + value, 0);
        const average = arraySum / array.length;
        setVolumeLevel(average);
      };

      // Saving context to close it once the recording is stopped
      setAudioContext(audioContext);
    }

    return new MediaRecorder(stream);
  }, [audioDeviceId, showMicVolumeLevel]);

  const initializeRecorder = useCallback(async () => {
    try {
      const recorderInstance = await requestRecorder();
      setRecorder(recorderInstance);
    } catch (error) {
      console.error(error);
    }
  }, [requestRecorder]);

  // this method gets fired when recorder.stop method is invoked
  const handleData = useCallback(
    async (e) => {
      if (!isOffline) {
        audioContext.close();
        // audio format
        const file = new File([e.data], "audio.wav", {
          type: "audio/wav",
          lastModified: Date.now(),
        });
        recordedAudioFile.current = file;

        if (!isValidAudio) {
          setErrorMessage(
            "No audio detected, please try changing your microphone or system setting"
          );
          setRecorder(null);
          setRecordTimer(timeToSolve || SPONTANEOUS_RECORDING_TIME);
          return;
        }
        triggerLoadingMsgUpdate("Taking a little longer", 5)
        // sending file data for backend apis
        onDataAvailable(file);

        // sending blob data for audioTestingModal component
        getBlobData(e.data);
      }
    },
    [audioContext, getBlobData, isValidAudio, onDataAvailable, isOffline]
  );

  const triggerLoadingMsgUpdate = (text = "", postSeconds = 0) => {
    const loadingMsgTimeout = setTimeout(() => {
      setLoadingMessage(text)
      clearTimeout(loadingMsgTimeout)
    }, postSeconds * 1000)
  }

  const startRecording = () => {
    setIsRecording(true);
    onStatusChange("recording");
  };

  const stopRecording = () => {
    setIsRecording(false);
    onStatusChange("inactive");
  };

  const onProceedAnyway = () => {
    triggerLoadingMsgUpdate("Taking a little longer", 5)
    if (onSubmit) onSubmit();
    if (onDataAvailable) onDataAvailable(recordedAudioFile.current);
  };

  useEffect(() => {
    if (timeToSolve)
      setRecordTimer(timeToSolve)
  }, [timeToSolve]);

  /**
   * Initialize timer on recording and get recording 59 seconds
   * After which make API call by clicking button throgh ref
   * Since API for speech checking restricted to file < 60 seconds.
   */
  useEffect(() => {
    if (hideTimer) return;

    let intervalId;
    if (isRecording) {
      // removed setInterval from here. Using web worker instead.
    }
    // In addition to recordTimer, if gameTime (quizTime) hits 0, stop recprdng and save answer.
    if (
      ((recordTimer <= 0) || (gameTime !== null && gameTime === 0))
      && buttonRef.current) {
      buttonRef.current.click();
    }
    return () => clearInterval(intervalId);
  }, [isRecording, recordTimer, hideTimer, gameTime]);

  // Refresh timer if loading changes state -> Only happens when error in audio upload
  useEffect(() => {
    setIsLoading(prevState => {
      if (prevState) setRecordTimer(timeToSolve || SPONTANEOUS_RECORDING_TIME);
      return loading;
    });
  }, [loading]);

  useEffect(() => {
    if (recorder === null) {
      if (isRecording) {
        initializeRecorder();
      }
      return;
    }

    // Manage recorder state.
    if (isRecording && recorder.state !== "recording") {
      recorder.start();

      // resuming audio context which give microphone volume info
      if (audioContext) audioContext.resume();
    } else if (!isRecording) {
      // execute stop only when state is inactive -> Android Fix
      recorder.state !== "inactive" && recorder.stop();

      // Suspends the progression of time in the audio context, temporarily halting audio hardware access and reducing CPU/battery usage in the process.
      if (audioContext) audioContext.suspend();
    }

    recorder.addEventListener("dataavailable", handleData);
    return () => {
      recorder.removeEventListener("dataavailable", handleData);
      // if (recorder.state === "recording") recorder.stop();
    };
  }, [recorder, isRecording, audioContext, handleData, initializeRecorder]);

  useEffect(() => {
    const cleanup = () => {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    };
    if (isValidAudio) {
      return () => cleanup();
    }

    if (volumeLevel > 10) {
      if (!timerRef.current) {
        timerRef.current = setTimeout(() => {
          setIsValidAudio(true);
        }, 500);
      }
    } else {
      cleanup();
    }
  }, [isValidAudio, volumeLevel]);

  useEffect(() => {
    if (isOffline) {
      setIsRecording(false)
      setRecordTimer(timeToSolve || SPONTANEOUS_RECORDING_TIME)
      setIsLoading(false)
    }
  }, [isOffline, timeToSolve])

  /**
   * Start the timer once user presses speak btn.
   */
  useEffect(() => {
    if (isRecording) {
      workerRef.current.postMessage("startTimer")
    } else {
      if (workerRef.current) {
        workerRef.current.postMessage("stopTimer")
      }
    }
  }, [isRecording])

  /**
   * Load the timer worker on component mount.
   * By default isTimerReady is false. It is set to true only after worker is loaded with "timerLoaded" event.
   * "tick" event is used to progress the recording timer.
   * Terminate worker on component unmount.
   */
  useEffect(() => {
    workerRef.current = new Worker(new URL("../../utils/timerWorker.js", import.meta.url))
    workerRef.current.onmessage = (event) => {
      if (event.data === "timerLoaded") {
        setIsTimerReady(true)
      } else if (event.data === "tick") {
        setRecordTimer((prevTime) => prevTime - 1)
      }
    }

    return () => {
      workerRef.current.terminate()
    }
  }, [])

  // Microphone volume blocks
  const audioBlocks = Array.from({ length: 6 }, (_, index) => {
    const numberOfBlocksToColor = Math.round(volumeLevel / 6);

    return (
      <div
        key={index}
        className="h-4 w-1 flex-1 rounded-[4px]"
        style={{
          backgroundColor:
            index < numberOfBlocksToColor ? "#FFF" : "rgba(255,255,255,0.3)",
        }}
      />
    );
  });

  return (
    <div
      className={classNames(`mt-10 flex flex-col items-center gap-4 min-h-[220px]`, {
        "relative mt-12": spotlightOrgFlag
      })}
    >
      {isRecording ? (
        <>
          <button
            className={classNames("flex items-center transition-all hover:shadow-md bg-bistre",
              {
                "bg-[#fff] rounded-full p-[30px] relative z-[1]": spotlightOrgFlag,
                "rounded-3xl py-2 px-4 border border-[#505050ef]": !spotlightOrgFlag
              }
            )}
            ref={buttonRef}
            onClick={stopRecording}
            disabled={isLoading}
          >
            {
              spotlightOrgFlag
                ? <Mic />
                : <></>
            }
            {showMicVolumeLevel && !spotlightOrgFlag && (
              <div className="flex items-center gap-1">{audioBlocks}</div>
            )
            }
            {
              !spotlightOrgFlag
                ? <>
                  <p className="text-[#FFF] ml-3">SUBMIT</p>
                  <img src={stopAudio} alt="stop" className="ml-3" />
                </>
                : <></>
            }
          </button>
          {
            spotlightOrgFlag
              ? <AudioRipple volumeLevel={volumeLevel} />
              : <></>
          }
          {
            spotlightOrgFlag
              ? <p className="text-[12px] text-[#fff] font-light opacity-60 uppercase">tap again to stop</p>
              : <></>
          }
          {!hideTimer && (
            <p className=" font text-sm">
              Recording time left:
              <span className="font-medium"> {recordTimer}</span>
            </p>
          )}

        </>
      ) : (
        <>
          <button
            className={classNames(
              "flex items-center transition-all hover:shadow-md border border-[#505050ef]",
              {
                "bg-gray-300 cursor-not-allowed": isLoading,
                "opacity-50 cursor-wait": !isTimerReady // Disabled styling while timer worker is getting loaded.
              },
              {
                "bg-[#fff] rounded-full p-[30px] relative z-[1]": spotlightOrgFlag,
                "rounded-3xl py-2 px-4": !spotlightOrgFlag
              }
            )}
            onClick={startRecording}
            disabled={isLoading || !isTimerReady} // Keep btn disabled while it is submitting audio and while timer worker is getting loaded.
          >
            <Mic />
            {
              !spotlightOrgFlag
                ? <p className="text-[#2E1500] font-medium ml-3">SPEAK</p>
                : <></>
            }
          </button>
          {
            spotlightOrgFlag
              ? <AudioRipple volumeLevel={volumeLevel} />
              : <></>
          }
          {
            spotlightOrgFlag
              ? <p className="text-[12px] font-light text-[#fff] opacity-60 uppercase">tap to record your answer</p>
              : <></>
          }
          {
            isLoading ?
              <div className="audio-submission flex items-end justify-start opacity-70">
                <p className={classNames("font-medium text-[16px] ml-2 relative bottom-[-5px] tracking-[0.3px]", {
                  "text-[#000]": !spotlightOrgFlag,
                  "text-[#fff] opacity-50": spotlightOrgFlag
                })}>
                  {loadingMessage}
                </p>
                <div className="typing__dot relative bottom-[-1px] scale-[0.6]" />
                <div className="typing__dot relative bottom-[-1px] scale-[0.6]" />
                <div className="typing__dot relative bottom-[-1px] scale-[0.6]" />
              </div> : <></>
          }
          {!!errorMessage && (
            <div className={classNames("flex flex-col items-center", {
              "hidden": isLoading
            })}>
              <p className="text-base text-center text-red-500 mb-4">{errorMessage}</p>
              <Button
                className={classNames({
                  "bg-[#fff]": spotlightOrgFlag
                })}
                onClick={onProceedAnyway}
              >
                Proceed Anyway
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
